'use client'

import React from 'react'
import { Media } from '@/payload-types'
import classNames from 'classnames'
import { Icon } from '@/ui/atoms/Icon'
import { useTranslations } from 'next-intl'
import { Avatar } from '@/ui/atoms/Avatar'
import { Tooltip } from '@/ui/atoms/Tooltip'
import { AuthorTooltipContent, MultipleAuthorsTooltipContent } from '@/ui/atoms/AuthorTooltip'

type SingleAuthorData = {
  name: string
  avatar?: Media | string | null
  verified?: boolean
  description?: string | null
  shortDescription?: string | null
  linkedin?: string | null
  /** author-page URL — avatar + name become links when set */
  href?: string | null
}

/** avatar box, linked to the author page when one exists */
const AuthorAvatar: React.FC<{ author: SingleAuthorData }> = ({ author }) => {
  const avatar = (
    <Avatar
      userImage={author.avatar}
      initials={author.name.charAt(0)}
      size="medium"
      variant="circle"
    />
  )
  return author.href ? (
    <a href={author.href} aria-label={author.name} className="block w-9 h-9">
      {avatar}
    </a>
  ) : (
    <div className="w-9 h-9">{avatar}</div>
  )
}

type AuthorProps = {
  authors: SingleAuthorData[]
  size?: 'small' | 'big'
  type?: 'verified' | 'info'
  className?: string
}

export const Author: React.FC<AuthorProps> = ({ authors = [], size = 'small', type = 'verified', className }) => {
  const t = useTranslations('public')

  const isMultipleAuthors = authors.length > 1
  const avatarSize = size === 'big' && !isMultipleAuthors ? 'medium' : 'mini'

  if (!authors.length) {
    return null
  }

  const firstAuthor = authors[0] as SingleAuthorData
  const otherAuthors = authors.slice(1)

  const verifiedBadge = firstAuthor.verified && type === 'verified' ? (
    <div className="flex items-center gap-1 text-xs text-text-subtle">
      <span className={'text-body-XS-regular text-text-faded'}>
        {authors.length > 1 ? t('verified-authors') : t('verified-author')}
      </span>
      <Icon name="check-circle" className="w-4 h-4 text-text-accent" />
    </div>
  ) : null

  if (isMultipleAuthors) {
    if (size === 'big') {
      return (
        <div className={classNames('flex flex-col gap-1', className)}>
          {verifiedBadge}
          <div className="flex flex-col gap-2">
            {authors.map((author, index) => (
              <div key={index} className="flex items-center gap-2">
                <AuthorAvatar author={author} />
                <div className="flex items-center gap-1">
                  <Tooltip
                    content={
                      <AuthorTooltipContent
                        name={author.name}
                        avatar={author.avatar}
                        description={author.description}
                        shortDescription={author.shortDescription}
                        linkedin={author.linkedin}
                        verified={author.verified}
                        appearance={type === 'verified' ? 'verified' : 'default'}
                      />
                    }
                    position="bottom"
                    className="cursor-pointer inline-block"
                  >
                    <div className="inline-block">
                      {author.href ? (
                        <a
                          href={author.href}
                          className="text-text-subtle text-label-XS-semibold canhover:hover:underline"
                        >
                          {author.name}
                        </a>
                      ) : (
                        <span className="text-text-subtle text-label-XS-semibold">{author.name}</span>
                      )}
                    </div>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div className={classNames('flex flex-col gap-0', className)}>
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {authors.slice(0, 2).map((author, index) => (
              <AuthorAvatar key={index} author={author} />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Tooltip
              content={
                <MultipleAuthorsTooltipContent
                  authors={authors.map((author) => ({
                    name: author.name,
                    avatar: author.avatar,
                    description: author.description,
                    shortDescription: author.shortDescription,
                    linkedin: author.linkedin,
                    verified: author.verified,
                    appearance: type === 'verified' ? 'verified' : 'default',
                  }))}
                />
              }
              position="bottom"
              className="cursor-pointer inline-block"
            >
              <div className="inline-block">
                <span className="text-text-subtle text-label-XS-semibold">
                  {firstAuthor.name} & {otherAuthors.length}{' '}
                  {otherAuthors.length > 1 ? t('others') : t('other')}
                </span>
              </div>
            </Tooltip>
            {authors.some((author) => author.verified) && type === 'verified' && (
              <Icon name="check-circle" className="w-4 h-4 text-text-accent" />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classNames('flex flex-col', size === 'big' ? 'gap-1' : 'gap-0', className)}>
      <div className="flex items-center gap-2">
        <AuthorAvatar author={firstAuthor} />
        <div
          className={classNames(
            'flex justify-center',
            size === 'big' ? 'flex-col gap-1' : 'items-center gap-1',
          )}
        >
          <div className="flex items-center gap-1">
            <Tooltip
              content={
                <AuthorTooltipContent
                  name={firstAuthor.name}
                  avatar={firstAuthor.avatar}
                  description={firstAuthor.description}
                  shortDescription={firstAuthor.shortDescription}
                  linkedin={firstAuthor.linkedin}
                  verified={firstAuthor.verified}
                  appearance={type === 'verified' ? 'verified' : 'default'}
                />
              }
              position="bottom"
              className="cursor-pointer inline-block"
            >
              <div className="inline-block">
                {firstAuthor.href ? (
                  <a
                    href={firstAuthor.href}
                    className="text-label-XS-semibold text-text-subtle canhover:hover:underline"
                  >
                    {firstAuthor.name}
                  </a>
                ) : (
                  <span className="text-label-XS-semibold text-text-subtle">{firstAuthor.name}</span>
                )}
              </div>
            </Tooltip>
            {firstAuthor.verified && size === 'small' && type === 'verified' && (
              <Icon name="check-circle" className="w-4 h-4 text-text-accent" />
            )}
          </div>
          {size === 'big' && verifiedBadge}
        </div>
      </div>
    </div>
  )
}

export default Author
